import React, {useContext, useState} from "react"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useAccessToken} from "../../services/auth"
import {AsyncResultModal} from "../AsyncResultModal/AsyncResultModal"
import {createOrgUserByOrgUser} from "../../resources/user"
import {nameRegex, Username} from "./Shared"
import {SelectUserRoleField, UserRolesExplanationTooltip} from "./UserRoleField"
import {fetchOrgId} from "../../resources/org"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import PhoneNumber from "../PhoneNumberLine/PhoneNumber"
import {getErrorMessage} from "../../utilities/validation"
import {claimsContext} from "../../services/claims"
import {getInvitableRolesForOrgUser} from "./userRoleMapping"

type CloseFunc = () => void
type RefreshFunc = () => void

interface ModalProps {
	close: CloseFunc
	refresh: RefreshFunc
}

function getCurrentUserRole(claims: {role?: string; roles?: string[]} | undefined): string | undefined {
	if (!claims) return undefined
	if (claims.roles && Array.isArray(claims.roles) && claims.roles.length > 0) {
		return claims.roles[0]
	}
	return claims.role ?? undefined
}

export function OrgNewUserModal({close, refresh}: ModalProps) {
	const accessToken = useAccessToken()
	const claims = useContext(claimsContext)
	const currentUserRole = getCurrentUserRole(claims)
	const invitableRoles = currentUserRole ? getInvitableRolesForOrgUser(currentUserRole) : []
	const defaultRole = invitableRoles[0] ?? "admin"
	const [state, create] = useAsyncResultIdle(createOrgUserByOrgUser)
	const [username, setUsername] = useState("")
	const [firstName, setFirstName] = useState("")
	const [lastName, setLastName] = useState("")
	const [role, setRole] = useState(defaultRole)
	const orgIdState = useAsyncResult(() => fetchOrgId(accessToken))
	const [countryCode, setCountryCode] = useState("")
	const [phoneNumber, setPhoneNumber] = useState("")
	const orgId = orgIdState.match(
		() => undefined,
		(o) => o,
		() => undefined
	)

	if (!currentUserRole) {
		console.error(
			"Cannot determine user role: claims are missing or do not contain a role. User creation requires a valid role to determine which roles can be invited."
		)
		return null
	}

	return (
		<AsyncResultModal
			title="Create new user"
			onSubmit={() => create(accessToken, username, firstName, lastName, role, {countryCode, number: phoneNumber})}
			close={close}
			state={state}
			buttonClassname="is-success"
			buttonText="Create"
			successText="Created"
			errorToText={(err) => getErrorMessage(err.errors[0])}
			onSuccess={refresh}
		>
			<Username username={username} setUsername={setUsername} />
			<div className="field">
				<label className="label">First Name</label>
				<div className="control">
					<input
						className="input"
						type="text"
						placeholder="First Name"
						value={firstName}
						pattern={nameRegex}
						title="No special characters are allowed"
						required
						onChange={(e) => setFirstName(e.target.value)}
					/>
				</div>
			</div>
			<div className="field">
				<label className="label">Last Name</label>
				<div className="control">
					<input
						className="input"
						type="text"
						placeholder="Last Name"
						value={lastName}
						pattern={nameRegex}
						title="No special characters are allowed"
						required
						onChange={(e) => setLastName(e.target.value)}
					/>
				</div>
			</div>
			<div className="field">
				<PhoneNumber
					countryCodeValue={countryCode}
					countryCodeOnChange={setCountryCode}
					phoneNumberValue={phoneNumber}
					phoneNumberOnChange={setPhoneNumber}
				/>
			</div>
			<div className="field">
				<label className="label">
					User Role <UserRolesExplanationTooltip type="orgUser" orgId={orgId} />
				</label>
				<SelectUserRoleField type="orgUser" role={role} setRole={setRole} orgId={orgId} allowedRoles={invitableRoles} />
			</div>
		</AsyncResultModal>
	)
}
